import { userQueryKeys } from '@/config/queryKeys';
import { useAgGridDatasourceRefresh } from '@/hooks';
import { getDateAsString } from '@/lib';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import type { PaginatedUsersResponse, UserPublic } from '@/types';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import { Avatar } from '@nautilusbio/nb-design-system/Avatar';
import Badge from '@nautilusbio/nb-design-system/Badge';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Link } from '@tanstack/react-router';
import type { FirstDataRenderedEvent, GridReadyEvent, PaginationChangedEvent } from 'ag-grid-community';
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { clsx } from 'clsx';
import React, { FC, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import tableStyles from '../../styles/components/table.module.css';
import globalStyles from '../../styles/index.module.css';

import OrgCell from './OrgCell';
import styles from './styles.module.css';
import SuperAdminToggleCell from './SuperAdminToggleCell';

export type SuperAdminGlobalTableProps = {
  gridRef: RefObject<AgGridReact<UserPublic> | null>;
  currentUserId: string;
  orgId: string;
  orgNameMap: Map<string, string>;
  onTotalCountChange?: (count: number) => void;
};

const SCROLL_STATE_KEY = 'users-sa-table-scroll-state';

type ScrollState = { page: number; scrollTop: number };

function readScrollState(): ScrollState | null {
  try {
    const raw = sessionStorage.getItem(SCROLL_STATE_KEY);
    return raw ? (JSON.parse(raw) as ScrollState) : null;
  } catch {
    return null;
  }
}

function writeScrollState(state: ScrollState): void {
  try {
    sessionStorage.setItem(SCROLL_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors (private mode, quota)
  }
}

const tableContainerClasses = { className: clsx(globalStyles.scrollbars, styles.table) };

const SuperAdminGlobalTable: FC<SuperAdminGlobalTableProps> = ({
  gridRef,
  currentUserId,
  orgId,
  orgNameMap,
  onTotalCountChange,
}) => {
  // Invalidates the TanStack Query datasource cache for every cached page/sort/filter
  // combination, then purges AG Grid's block cache and triggers a full reload.
  // Using refreshServerSide({ purge: false }) alone returns stale data because
  // createAgGridDatasource caches responses with a 1-day staleTime — invalidation
  // must happen first so getRows fetches fresh data from the server.
  const handleMutationSettled = useAgGridDatasourceRefresh(userQueryKeys.endpoints.globalSearch(), gridRef);

  const { t: tUsers } = useTranslation('users');
  const { t: tGeneral } = useTranslation('general');

  const colDefs = useMemo<ColDef<UserPublic>[]>(
    () => [
      // full_name with avatar
      {
        cellRenderer: ({ data }: { data: UserPublic }) => (
          <Stack alignItems="center" gap={4} orientation="horizontal">
            <Avatar size="xs2">{data.full_name}</Avatar>
            <Link to="/users/$userId" params={{ userId: data.id }}>
              <Text as="span" className={getTextClassName('sm')}>
                {data.full_name}
              </Text>
            </Link>
          </Stack>
        ),
        field: 'full_name',
        headerName: tGeneral('name'),
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'endsWith', 'blank', 'notBlank'],
          maxNumConditions: 1,
          buttons: ['reset', 'apply'],
        },
        minWidth: 200,
      },

      // email
      {
        field: 'email',
        headerName: tGeneral('email'),
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'endsWith', 'blank', 'notBlank'],
          maxNumConditions: 1,
          buttons: ['reset', 'apply'],
        },
        minWidth: 220,
      },

      // Create Studies (placeholder — not yet in UserPublic)
      {
        colId: 'can_create_studies',
        // TODO: wire to backend permission field when available on UserPublic
        headerName: tUsers('can_create_studies_column_header'),
        sortable: false,
        filter: false,
        valueGetter: () => null,
        valueFormatter: () => '—',
        maxWidth: 140,
      },

      // Create Runs (placeholder — not yet in UserPublic)
      {
        colId: 'can_create_runs',
        // TODO: wire to backend permission field when available on UserPublic
        headerName: tUsers('can_create_runs_column_header'),
        sortable: false,
        filter: false,
        valueGetter: () => null,
        valueFormatter: () => '—',
        maxWidth: 120,
      },

      // is_super_admin (platform-level badge)
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: ({ value }: { value: boolean }) => (
          <Badge className={styles.badge} size="xs" variant={value ? 'success' : 'default'}>
            {value ? tUsers('is_super_admin_yes') : tUsers('is_super_admin_no')}
          </Badge>
        ),
        field: 'is_super_admin',
        headerName: tUsers('is_super_admin_column_header'),
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          valueFormatter: ({ value }: { value: boolean }) =>
            value ? tUsers('is_super_admin_yes') : tUsers('is_super_admin_no'),
          buttons: ['reset', 'apply'],
        },
        maxWidth: 180,
      },

      // SuperAdmin org-toggle column
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: ({ data }: { data: UserPublic }) => (
          <SuperAdminToggleCell
            currentUserId={currentUserId}
            isSuperAdminOverride={data.is_super_admin}
            onMutationSettled={handleMutationSettled}
            orgId={orgId}
            user={data}
          />
        ),
        colId: 'super_admin_toggle',
        field: 'is_super_admin' as const,
        filter: false,
        width: 80,
        headerName: tUsers('super_admin_column_header'),
        sortable: false,
      },

      // Organization — resolved from allowed_roles keys via OrgCell
      {
        colId: 'organization',
        headerName: tUsers('organization_column_header'),
        hide: true,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }: { data: UserPublic }) => (
          <OrgCell orgIds={Object.keys(data?.allowed_roles ?? {})} orgNameMap={orgNameMap} />
        ),
      },

      // last_login_at — not in UserSortField; sort/filter disabled
      {
        field: 'last_login_at',
        headerName: tUsers('last_login_at_column_header'),
        sortable: false,
        filter: false,
        valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : '—'),
        maxWidth: 180,
      },

      // updated_at
      {
        field: 'updated_at',
        headerName: tUsers('updated_at_column_header'),
        sortable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
          maxNumConditions: 2,
          buttons: ['reset', 'apply'],
        },
        valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : '—'),
        maxWidth: 210,
      },

      // updated_by_full_name — not in UserSortField; sort/filter disabled
      {
        field: 'updated_by_full_name',
        headerName: tGeneral('updated_by'),
        hide: true,
        sortable: false,
        filter: false,
        valueFormatter: ({ value }) => value ?? '—',
        maxWidth: 200,
      },

      // created_at
      {
        field: 'created_at',
        headerName: tGeneral('created_at'),
        sortable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
          maxNumConditions: 2,
          buttons: ['reset', 'apply'],
        },
        valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : '—'),
        maxWidth: 210,
      },

      // created_by_full_name — not in UserSortField; sort/filter disabled
      {
        field: 'created_by_full_name',
        headerName: tGeneral('created_by'),
        hide: true,
        sortable: false,
        filter: false,
        valueFormatter: ({ value }) => value ?? '—',
        maxWidth: 200,
      },
    ],
    [tGeneral, tUsers, currentUserId, orgId, handleMutationSettled, orgNameMap],
  );

  // AG Grid server-side datasource via POST /users/search
  // is_super_admin is a boolean field — AG Grid's Set Filter serialises filter
  // model values as strings, so we declare it here to coerce "true"/"false"
  // back to actual booleans before the payload is sent to the API.
  const datasource = useMemo(
    () =>
      createAgGridDatasource<UserPublic, PaginatedUsersResponse>(userQueryKeys.endpoints.globalSearch(), undefined, {
        booleanFields: ['is_super_admin'],
      }),
    [],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource, gridRef]);

  // Holds scroll state to restore; cleared once restoration fires in onFirstDataRendered.
  const pendingScrollRef = useRef<ScrollState | null>(null);
  // True while we are programmatically restoring scroll so onFirstDataRendered
  // doesn't schedule a scroll that conflicts with the ensureIndexVisible call.
  const isRestoringRef = useRef(false);

  const handleGridReady = useCallback(
    (e: GridReadyEvent<UserPublic>) => {
      e.api.setGridOption('serverSideDatasource', datasource);
      sizeColumnsOnFirstVisit(e.api, 'super-admin-users-table');

      const saved = readScrollState();
      if (!saved) return;

      sessionStorage.removeItem(SCROLL_STATE_KEY);
      isRestoringRef.current = true;
      pendingScrollRef.current = saved;

      if (saved.page > 0) {
        e.api.paginationGoToPage(saved.page);
      }
      // scrollTop is restored in onFirstDataRendered once data is painted.
    },
    [datasource],
  );

  // ensureIndexVisible is only valid after firstDataRendered. For SSRM + pagination,
  // firstDataRendered fires once the correct page's rows are in the DOM — whether
  // that is page 0 (default) or a non-zero page set via paginationGoToPage in
  // onGridReady — so this is the correct place for both cases.
  const handleFirstDataRendered = useCallback((_e: FirstDataRenderedEvent<UserPublic>) => {
    const pending = pendingScrollRef.current;
    if (!pending) return;

    pendingScrollRef.current = null;
    // Directly set scrollTop on the AG Grid viewport. ensureIndexVisible uses row
    // indices which don't work when all rows are rendered (no virtual window), but
    // scrollTop is always reliable. There is exactly one grid on this route.
    const vp = document.querySelector<HTMLElement>('.ag-body-viewport');
    if (vp) vp.scrollTop = pending.scrollTop;
    setTimeout(() => {
      isRestoringRef.current = false;
    }, 300);
  }, []);

  const handlePaginationChanged = useCallback(
    (e: PaginationChangedEvent<UserPublic>) => {
      const count = e.api.paginationGetRowCount();
      if (count > 0) onTotalCountChange?.(count);
    },
    [onTotalCountChange],
  );

  return (
    <Table
      columnDefs={colDefs}
      container={tableContainerClasses}
      domLayout="normal"
      enableResponsiveResize={false}
      onFirstDataRendered={handleFirstDataRendered}
      onGridReady={handleGridReady}
      onPaginationChanged={handlePaginationChanged}
      pagination
      paginationPageSize={25}
      paginationPageSizeSelector={[25, 50, 100]}
      ref={gridRef}
      resizeDebounceMs={150}
      rowModelType="serverSide"
      tableId="super-admin-users-table"
      onGridPreDestroyed={e => {
        flushColumnState(e.api, 'super-admin-users-table');
        if (!isRestoringRef.current) {
          const scrollTop = e.api.getVerticalPixelRange().top;
          const page = e.api.paginationGetCurrentPage();
          writeScrollState({ page, scrollTop });
        }
      }}
      zebraStriping
    />
  );
};

export default SuperAdminGlobalTable;
